import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    FiPieChart, FiRefreshCw, FiAlertTriangle, FiCheckCircle, FiBookOpen,
    FiUsers, FiCalendar, FiClock, FiArrowRight, FiPauseCircle
} from 'react-icons/fi';
import { getCourses, getSchedules, getInstructors } from '../services';
import toast from 'react-hot-toast';

const fmtDate = (d) => {
    if (!d) return '—';
    const date = d instanceof Date ? d : new Date(d);
    return date.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' });
};

const isActiveCourse = (c) => (c.estado || 'activo') !== 'inactivo';

const PlanningPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [courses, setCourses] = useState([]);
    const [schedules, setSchedules] = useState([]);
    const [instructors, setInstructors] = useState([]);

    const loadData = async () => {
        try {
            const [c, s, i] = await Promise.all([getCourses(), getSchedules(), getInstructors()]);
            setCourses(c);
            setSchedules(s);
            setInstructors(i);
        } catch (err) {
            console.error(err);
            toast.error('Error cargando la planificación');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => { loadData(); }, []);

    const refresh = () => { setRefreshing(true); loadData(); };

    const getInstructor = (id) => instructors.find(i => i.id === id);

    // ── Cálculo principal ────────────────────────────────────────────────────
    const data = useMemo(() => {
        const now = new Date();

        // Agrupar eventos visibles (no cancelados) por curso
        const byCourse = {};
        for (const ev of schedules) {
            if (ev.status === 'cancelled') continue;
            const cid = ev.courseId || '_none';
            (byCourse[cid] = byCourse[cid] || []).push(ev);
        }

        const enriched = courses.map(course => {
            const evts = byCourse[course.id] || [];
            const future = evts.filter(e => (e.end instanceof Date ? e.end : new Date(e.end)) >= now);
            const hasScheduling = evts.length > 0;
            const active = isActiveCourse(course);

            // Instructor: el del curso, o el más frecuente en sus eventos
            let instructorId = course.instructorId || null;
            if (!instructorId && evts.length) {
                const counts = {};
                evts.forEach(e => { if (e.instructorId) counts[e.instructorId] = (counts[e.instructorId] || 0) + 1; });
                instructorId = Object.keys(counts).sort((a, b) => counts[b] - counts[a])[0] || null;
            }

            const nextEvent = future
                .slice()
                .sort((a, b) => new Date(a.start) - new Date(b.start))[0] || null;

            return {
                ...course,
                _active: active,
                _hasScheduling: hasScheduling,
                _totalEvents: evts.length,
                _futureCount: future.length,
                _instructorId: instructorId,
                _nextDate: nextEvent ? nextEvent.start : null,
            };
        });

        const activos = enriched.filter(c => c._active);
        const activosSinProg = activos.filter(c => !c._hasScheduling);
        const activosEnCurso = activos.filter(c => c._futureCount > 0);
        // Finalizados: tienen eventos pero ninguno futuro (no cuentan como "programados").
        const activosFinalizados = activos.filter(c => c._hasScheduling && c._futureCount === 0);
        const inactivos = enriched.filter(c => !c._active);

        // Instructores con cursos activos (programados o no)
        const instructorMap = {};
        for (const c of activos) {
            if (!c._instructorId) continue;
            (instructorMap[c._instructorId] = instructorMap[c._instructorId] || []).push(c);
        }
        const porInstructor = Object.entries(instructorMap)
            .map(([id, list]) => ({ instructor: getInstructor(id), cursos: list }))
            .filter(x => x.instructor)
            .sort((a, b) => b.cursos.length - a.cursos.length);

        const sinInstructor = activos.filter(c => !c._instructorId).length;

        return {
            enriched, activos, activosSinProg, activosEnCurso, activosFinalizados, inactivos,
            porInstructor, sinInstructor,
            instructoresActivos: porInstructor.length,
        };
    }, [courses, schedules, instructors]);

    if (loading) {
        return (
            <div className="h-full bg-white">
                <div className="fixed top-0 left-0 w-full h-1 z-50 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-500 animate-loading-bar" />
                </div>
            </div>
        );
    }

    const StatCard = ({ icon, value, label, sub, accent, alert }) => (
        <div className={`bg-white p-5 rounded-xl shadow-sm border ${alert ? 'border-red-200' : 'border-gray-100'}`}>
            <div className="flex items-center justify-between mb-2">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${accent}`}>{icon}</div>
                {alert && value > 0 && (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700 animate-pulse">Acción</span>
                )}
            </div>
            <p className="text-2xl font-bold text-gray-800">{value}</p>
            <p className="text-sm text-gray-500">{label}</p>
            {sub && <p className="mt-1 text-xs text-gray-400">{sub}</p>}
        </div>
    );

    return (
        <div className="p-6 max-w-[1400px] mx-auto">
            {/* Header */}
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
                        <FiPieChart className="text-indigo-600" />
                        Planificación
                    </h1>
                    <p className="text-gray-500">Resumen de cursos activos, programación e instructores</p>
                </div>
                <button onClick={refresh}
                    className={`p-2 text-gray-500 hover:bg-gray-100 rounded-full transition ${refreshing ? 'animate-spin' : ''}`}
                    title="Actualizar">
                    <FiRefreshCw />
                </button>
            </div>

            {/* Stat cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <StatCard icon={<FiBookOpen className="text-white" size={18} />} accent="bg-green-500"
                    value={data.activos.length} label="Cursos activos"
                    sub={`${data.inactivos.length} inactivos`} />
                <StatCard icon={<FiCheckCircle className="text-white" size={18} />} accent="bg-indigo-500"
                    value={data.activosEnCurso.length} label="Activos en curso"
                    sub={`${data.activosFinalizados.length} finalizado(s)`} />
                <StatCard icon={<FiAlertTriangle className="text-white" size={18} />} accent="bg-red-500" alert
                    value={data.activosSinProg.length} label="Activos sin programación"
                    sub="Falta generar horario" />
                <StatCard icon={<FiUsers className="text-white" size={18} />} accent="bg-purple-500"
                    value={data.instructoresActivos} label="Instructores con cursos"
                    sub={data.sinInstructor ? `${data.sinInstructor} curso(s) sin instructor` : 'Todos asignados'} />
            </div>

            {/* Activos sin programación — acción */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6 overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2 bg-red-50/50">
                    <FiAlertTriangle className="text-red-500" />
                    <h3 className="font-bold text-gray-800">Cursos activos sin programación</h3>
                    <span className="text-xs text-gray-400">({data.activosSinProg.length})</span>
                </div>
                {data.activosSinProg.length === 0 ? (
                    <div className="px-5 py-8 text-center text-gray-400">
                        <FiCheckCircle size={40} className="mx-auto mb-2 text-green-400" />
                        <p className="text-sm">Todos los cursos activos tienen eventos programados 🎉</p>
                    </div>
                ) : (
                    <div className="divide-y divide-gray-50">
                        {data.activosSinProg.map(course => {
                            const inst = getInstructor(course._instructorId);
                            return (
                                <div key={course.id} className="px-5 py-3 flex items-center gap-3 hover:bg-gray-50/60">
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-semibold text-gray-800 truncate">{course.name}</p>
                                        <p className="text-xs text-gray-400 flex items-center gap-1.5 mt-0.5">
                                            {inst ? (
                                                <>
                                                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: inst.color || '#6b7280' }} />
                                                    {inst.name}
                                                </>
                                            ) : <span className="text-orange-500">Sin instructor asignado</span>}
                                        </p>
                                    </div>
                                    <button onClick={() => navigate('/courses')}
                                        className="shrink-0 flex items-center gap-1.5 text-xs font-semibold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 px-3 py-1.5 rounded-lg transition">
                                        Programar <FiArrowRight size={12} />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Activos programados */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
                        <FiCalendar className="text-indigo-500" />
                        <h3 className="font-bold text-gray-800">Cursos activos en curso</h3>
                        <span className="text-xs text-gray-400">({data.activosEnCurso.length})</span>
                    </div>
                    {data.activosEnCurso.length === 0 ? (
                        <div className="px-5 py-8 text-center text-gray-400 text-sm">Aún no hay cursos con eventos próximos</div>
                    ) : (
                        <div className="divide-y divide-gray-50 max-h-[28rem] overflow-y-auto">
                            {data.activosEnCurso
                                .slice()
                                .sort((a, b) => (a._nextDate ? new Date(a._nextDate) : Infinity) - (b._nextDate ? new Date(b._nextDate) : Infinity))
                                .map(course => {
                                    const inst = getInstructor(course._instructorId);
                                    return (
                                        <div key={course.id} className="px-5 py-3 flex items-center gap-3 hover:bg-gray-50/60">
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-semibold text-gray-800 truncate">{course.name}</p>
                                                <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-400">
                                                    {inst && (
                                                        <span className="flex items-center gap-1">
                                                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: inst.color || '#6b7280' }} />
                                                            {inst.name}
                                                        </span>
                                                    )}
                                                    <span className="flex items-center gap-1">
                                                        <FiClock size={10} /> {course._futureCount > 0 ? `próx. ${fmtDate(course._nextDate)}` : 'finalizado'}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="shrink-0 text-right">
                                                <p className="text-sm font-bold text-indigo-600">{course._futureCount}</p>
                                                <p className="text-[10px] text-gray-400">próximos</p>
                                            </div>
                                        </div>
                                    );
                                })}
                        </div>
                    )}
                </div>

                {/* Por instructor */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
                        <FiUsers className="text-purple-500" />
                        <h3 className="font-bold text-gray-800">Cursos activos por instructor</h3>
                        <span className="text-xs text-gray-400">({data.porInstructor.length})</span>
                    </div>
                    {data.porInstructor.length === 0 ? (
                        <div className="px-5 py-8 text-center text-gray-400 text-sm">Sin instructores asignados a cursos activos</div>
                    ) : (
                        <div className="divide-y divide-gray-50 max-h-[28rem] overflow-y-auto">
                            {data.porInstructor.map(({ instructor, cursos }) => {
                                const sinProg = cursos.filter(c => !c._hasScheduling).length;
                                return (
                                    <div key={instructor.id} className="px-5 py-3">
                                        <div className="flex items-center gap-3">
                                            <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0"
                                                style={{ backgroundColor: instructor.color || '#6b7280' }}>
                                                {instructor.name?.charAt(0)?.toUpperCase()}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-semibold text-gray-800 truncate">{instructor.name}</p>
                                                <p className="text-xs text-gray-400">
                                                    {cursos.length} curso{cursos.length !== 1 ? 's' : ''} activo{cursos.length !== 1 ? 's' : ''}
                                                    {sinProg > 0 && <span className="text-red-500"> · {sinProg} sin programar</span>}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap gap-1.5 mt-2 pl-12">
                                            {cursos.map(c => (
                                                <span key={c.id}
                                                    className={`text-[11px] px-2 py-0.5 rounded-full border ${c._hasScheduling ? 'bg-gray-50 border-gray-200 text-gray-600' : 'bg-red-50 border-red-200 text-red-600'}`}>
                                                    {c.name}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* Inactivos — resumen */}
            {data.inactivos.length > 0 && (
                <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-100 px-5 py-4 flex items-center gap-3">
                    <FiPauseCircle className="text-gray-400" size={20} />
                    <p className="text-sm text-gray-500">
                        <span className="font-semibold text-gray-700">{data.inactivos.length}</span> curso(s) inactivo(s) (sus clases están ocultas del calendario)
                    </p>
                </div>
            )}
        </div>
    );
};

export default PlanningPage;
